import type { Route } from "./+types/article";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import Markdown from "../components/content/Markdown/Markdown";
import VideoPlayer from "../components/media/VideoPlayer";
import ContextualCTA from "../components/content/ContextualCTA/ContextualCTA";
import SocialShare from "../components/content/SocialShare/SocialShare";
import InlineSubscribe from "../components/content/InlineSubscribe/InlineSubscribe";
import { subscribePitch } from "../config/subscribePitch";
import { content, trackView } from "../lib/content";
import { liveOffers } from "../lib/offers";
import { pageMeta } from "../lib/meta";
import { articleSchema, breadcrumbSchema, SITE_URL } from "../components/content/SEO/SEO";
import { categoryAccent, categoryLabel } from "../utils/categoryAccent";
import { extractToc, type TocItem } from "../utils/toc";
import { formatUtcShortDate } from "../utils/formatDate";
import { isScheduled } from "../utils/scheduled";
import { AFFILIATE_DISCLOSURE } from "../config/affiliate";
import { ContentColumn } from "../components/layout/ContentColumn/ContentColumn";

export async function loader({ params }: Route.LoaderArgs) {
  // liveOffers() is cached and never rejects, so the footer CTA can't take an
  // article page down or add a round-trip to every view.
  const [article, offers] = await Promise.all([content.article(params.page, params.slug), liveOffers()]);
  return { article, offers };
}

export function meta({ data }: Route.MetaArgs) {
  const a = data?.article;
  if (!a) return [{ title: "Not found | Gazar" }];
  const title = a.title.trim();
  const path = `/${a.category}/${a.slug}`;
  const img = (a.image as any)?.url;

  // JSON-LD: every article gets an Article + BreadcrumbList; reviews additionally
  // get a Review node so Google can show star ratings.
  const jsonLd: Record<string, any>[] = [
    articleSchema({ title, description: a.description ?? "", path, image: img, publishedAt: a.publishedAt, updatedAt: a.updatedAt }),
    breadcrumbSchema([
      { name: "Home", url: SITE_URL },
      { name: a.category.replace(/-/g, " "), url: `${SITE_URL}/${a.category}` },
      { name: title, url: `${SITE_URL}${path}` },
    ]),
  ];
  const r = a.review;
  if (r && r.rating) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Review",
      name: title,
      url: `${SITE_URL}${path}`,
      author: { "@type": "Person", name: "Ehsan Gazar" },
      datePublished: a.publishedAt,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      itemReviewed: {
        "@type": r.subjectAuthor ? "Book" : "Thing",
        name: r.subjectTitle ?? title,
        ...(r.subjectAuthor ? { author: { "@type": "Person", name: r.subjectAuthor } } : {}),
        ...(r.subjectUrl ? { url: r.subjectUrl } : {}),
      },
    });
  }

  return [
    ...pageMeta({ title: `${title} | Gazar`, description: a.description ?? "", path, image: img, type: "article", jsonLd }),
    // Scheduled: reachable by URL before its date (link previews need the OG
    // tags above), but not to be indexed until it is actually out.
    ...(isScheduled(a.publishedAt) ? [{ name: "robots", content: "noindex" }] : []),
    { property: "article:published_time", content: a.publishedAt },
    { property: "article:author", content: `${SITE_URL}/about` },
  ];
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} viewBox="0 0 20 20" className={`w-5 h-5 ${n <= rating ? "text-amber-500" : "text-brand-navy/20"}`} fill="currentColor" aria-hidden="true">
          <path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
        </svg>
      ))}
    </span>
  );
}

function ReviewCard({ review }: { review: NonNullable<Route.ComponentProps["loaderData"]["article"]["review"]> }) {
  const hasSubject = review.subjectTitle || review.subjectAuthor;
  return (
    <aside className="my-8 rounded-xl border border-brand-navy/15 bg-brand-navy/[0.03] p-5 sm:flex sm:items-center sm:justify-between gap-4">
      <div>
        {review.rating ? (
          <div className="flex items-center gap-2">
            <Stars rating={review.rating} />
            <span className="text-brand-navy font-semibold">{review.rating}/5</span>
          </div>
        ) : null}
        {hasSubject ? (
          <p className="text-brand-navy/70 mt-1 text-[15px]">
            A review of{" "}
            {review.subjectUrl ? (
              <a href={review.subjectUrl} target="_blank" rel="noopener noreferrer" className="font-medium underline decoration-brand-navy/40 hover:decoration-brand-navy/80">
                {review.subjectTitle}
              </a>
            ) : (
              <span className="font-medium">{review.subjectTitle}</span>
            )}
            {review.subjectAuthor ? ` by ${review.subjectAuthor}` : ""}
          </p>
        ) : null}
      </div>
      {review.buyUrl ? (
        <div className="mt-3 sm:mt-0 shrink-0 sm:text-right">
          <a href={review.buyUrl} target="_blank" rel="noopener noreferrer sponsored"
            className="inline-flex items-center justify-center rounded-lg bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-navy/90">
            Buy it
          </a>
          {/* Required affiliate disclosure (ASA/FTC + Amazon Operating Agreement)
              shown wherever the affiliate link appears. */}
          <p className="mt-1.5 max-w-[15rem] text-[11px] leading-snug text-brand-navy/50 sm:ml-auto">
            {AFFILIATE_DISCLOSURE}
          </p>
        </div>
      ) : null}
    </aside>
  );
}

// Thin fill bar showing how far through the article the reader is. Client-only
// (renders 0% on the server so hydration matches), tracks scroll over #article.
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const el = document.getElementById("article");
    if (!el) return;
    const update = () => {
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-el.getBoundingClientRect().top, 0), Math.max(total, 0));
      setPct(total > 0 ? Math.round((scrolled / total) * 100) : 0);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-heading text-overline uppercase tracking-[0.14em] text-brand-navy/45">Progress</span>
        <span className="text-caption tabular-nums text-brand-navy/45">{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-brand-navy/10">
        <div className="h-full rounded-full bg-brand-navy transition-[width] duration-150" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TableOfContents({ items }: { items: TocItem[] }) {
  return (
    <nav aria-label="Table of contents">
      <p className="mb-2 font-heading text-overline uppercase tracking-[0.14em] text-brand-navy/45">In this article</p>
      <ul className="space-y-1 border-l border-brand-navy/12">
        {items.map((it) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              className={`block border-l-2 border-transparent py-0.5 text-body-sm leading-snug text-brand-navy/65 transition-colors hover:border-brand-navy/60 hover:text-brand-navy ${it.depth === 3 ? "pl-6" : "pl-4"}`}
            >
              {it.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function Article({ loaderData }: Route.ComponentProps) {
  const { article, offers } = loaderData;

  // Count the read, once per article per mount, from the browser. Deliberately
  // not in the loader: that would count every crawler fetch, and roughly two
  // thirds of raw traffic here is datacenter bots. The API dedupes to one view
  // per visitor per day, so this is safe to fire on every navigation. A
  // scheduled article is not out yet, so an early reader is not a read.
  const scheduled = isScheduled(article.publishedAt);
  useEffect(() => {
    if (!scheduled) trackView(article.id);
  }, [article.id, scheduled]);

  // Both halves of #418 in one line. Pinning the zone was the first half: without
  // it the server (UTC) and the client (the visitor's zone) format a near-midnight
  // timestamp to different days. The second half was the locale data, which this
  // line kept until 2 Sep 2026: en-GB abbreviates September as "Sept" on Node and
  // "Sep" on WebKit, so every Safari threw this page away. formatUtcShortDate owns
  // the month names and reads UTC, so neither half can come back.
  const published = formatUtcShortDate(article.publishedAt);
  const review = article.review;
  const showReview = review && (review.rating != null || review.subjectTitle || review.buyUrl);
  const toc = extractToc(article.content);
  // One pitch for both capture surfaces on the page, so the end-of-read card
  // and the rail never make two different promises to the same reader.
  const pitch = subscribePitch(article.category);

  return (
    <ContentColumn anchor="article-main" className="mx-auto max-w-3xl px-4 py-12 lg:grid lg:max-w-6xl lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-12">
      <article id="article" className="min-w-0">
        <p className="mb-3">
          <Link to={`/${article.category}`} className={`inline-block text-sm font-semibold uppercase tracking-wider px-2.5 py-1 rounded ${categoryAccent(article.category).tag}`}>
            {categoryLabel(article.category)}
          </Link>
        </p>
        <h1 className="font-sans text-[40px] sm:text-[52px] font-bold text-brand-navy leading-[1.1] tracking-[-0.022em] mb-4">
          {article.title.trim()}
        </h1>
        {article.description ? <p className="text-[21px] text-brand-navy/70 mb-4">{article.description}</p> : null}
        <p className="text-sm text-brand-navy/50 mb-6">{published}</p>

        {article.video?.url ? (
          <div className="mb-4">
            <VideoPlayer
              source={{ type: 'file', src: article.video.url }}
              poster={article.video.poster ?? (article.image as any)?.url ?? null}
              aspectRatio={article.video.aspectRatio ?? '16 / 9'}
              track={{
                id: `article:${article.slug}`,
                title: article.title.trim(),
                surface: 'article_hero',
                props: { articleSlug: article.slug, category: article.category },
              }}
            />
          </div>
        ) : (article.image as any)?.url ? (
          <img
            src={(article.image as any).url}
            alt={article.title.trim()}
            className="w-full aspect-[16/9] object-cover rounded-xl border border-brand-navy/10 bg-brand-navy/5 mb-4"
            loading="eager"
          />
        ) : null}

        {showReview ? <ReviewCard review={review} /> : null}

        <Markdown content={article.content} />

        {/* End-of-read email capture (owned list). Articles are the Google-
            traffic surface, so this is the cheapest lead-capture placement.
            Tagged per-slug so the admin shows which posts actually convert,
            not just that "articles" did. The wording is matched to the
            category (config/subscribePitch); the tag deliberately is not, so
            this reads against its own before-and-after baseline. */}
        <InlineSubscribe source={`article:${article.slug}`} pitch={pitch} />

        <ContextualCTA category={article.category} offers={offers} />

        {article.related.length > 0 ? (
          <section className="mt-16 pt-10 border-t border-brand-navy/15">
            <h2 className="text-2xl font-bold text-brand-navy mb-5">Keep reading</h2>
            <ul className="space-y-3">
              {article.related.map((r) => (
                <li key={`${r.category}/${r.slug}`}>
                  <Link to={`/${r.category}/${r.slug}`} className="text-brand-navy underline decoration-brand-navy/40 hover:decoration-brand-navy/80">
                    {r.title.trim()}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>

      {/* Sticky right rail — desktop only. Uses the empty right half for a TOC,
          reading progress, and share; the article stays a comfortable measure. */}
      <aside className="hidden lg:block">
        {/* Cap the sticky rail to the viewport and let it scroll internally when
            it overflows. A sticky element taller than its scroll space pins its
            top and leaves the bottom (newsletter/share) permanently unreachable;
            max-height + overflow keeps every item reachable and adapts as the rail
            grows. On tall screens it never overflows, so this is a no-op there. */}
        <div className="sticky top-24 max-h-[calc(100dvh_-_7rem)] space-y-5 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin]">
          <ReadingProgress />
          {toc.length >= 2 ? <TableOfContents items={toc} /> : null}
          <div className="border-t border-brand-navy/10 pt-5">
            <InlineSubscribe source={`article-rail:${article.slug}`} variant="rail" pitch={pitch} />
          </div>
          <div className="border-t border-brand-navy/10 pt-5">
            <SocialShare title={article.title.trim()} description={article.description ?? undefined} />
          </div>
        </div>
      </aside>
    </ContentColumn>
  );
}
